<template>
	<div class="row">
		<!-- Le document reste sous les yeux pendant la correction : une ligne ne se vérifie qu'en la
		     relisant sur la facture. Il passe au-dessus du tableau sur les écrans étroits, où les deux
		     ne tiennent pas côte à côte. -->
		<div class="col-12 col-xl-5">
			<div class="box invoice-supplier-details__document">
				<iframe v-if="is_pdf" :src="invoice.suppliersocr_pdf_url" class="invoice-supplier-fiche__preview"></iframe>
				<img v-else-if="invoice.suppliersocr_pdf_url" :src="invoice.suppliersocr_pdf_url" class="w-100" />
				<p v-else class="mb-0">{{ $t('invoice.supplier_fiche_no_preview') }}</p>
			</div>
		</div>

		<div class="col-12 col-xl-7">
			<div class="box invoice-supplier-details">
			<LoadingSpinner v-if="!loaded" />

			<template v-else>
				<p v-if="read_only" class="invoice-supplier-details__hint">
					{{ $t('invoice.supplier_details_from_xml') }}
				</p>

				<div class="table-responsive">
					<table class="table mb-0 invoice-supplier-details__table">
						<thead>
							<tr>
								<th class="text-right">{{ $t('invoice.supplier_details_qty') }}</th>
								<th>{{ $t('invoice.supplier_details_unit') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_unit_price') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_ht') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_vat') }}</th>
								<th></th>
							</tr>
						</thead>

						<tbody v-if="!lines.length">
							<tr>
								<td :colspan="columns_count" class="text-center">{{ $t('invoice.supplier_details_empty') }}</td>
							</tr>
						</tbody>

						<!-- Une ligne de facture occupe deux rangées : son libellé sur toute la largeur,
						     parce qu'il est long et que c'est lui qu'on relit sur le document, puis ses
						     chiffres en dessous. Un tbody par ligne les garde solidaires. -->
						<tbody
							v-for="(line, index) in lines"
							:key="line.isdetails_id || 'new-' + index"
							class="invoice-supplier-details__line"
							:class="{ 'invoice-supplier-details__line--mismatch': hasAmountMismatch(line) }"
						>
							<tr>
								<td :colspan="columns_count" class="invoice-supplier-details__label">
									<div class="row">
										<div class="col-12 col-lg-7">
											<span v-if="line.locked">{{ line.isdetails_label }}</span>
											<b-input
												v-else
												v-model="line.isdetails_label"
												:placeholder="$t('invoice.supplier_details_label')"
												:state="line.isdetails_label ? null : false"
												@change="onLabelChanged(line)"
											/>
										</div>
										<!-- L'article se nomme à part du libellé. Sur une facture électronique, c'est
										     le seul champ de la rangée qui s'ouvre : le vendeur possède son libellé,
										     nous possédons le nom sous lequel nous le classons.

										     La liste propose ceux que ce fournisseur a déjà facturés — reprendre l'un
										     d'eux à l'identique suffit à rapprocher la ligne — mais reste ouverte à un
										     nom inédit, que la première facture d'un article est seule à porter. -->
										<div class="col-12 col-lg-5">
											<e-select
												:value="line.article_label"
												:options="article_labels"
												:taggable="true"
												:show-labels="false"
												:placeholder="$t('invoice.supplier_details_article')"
												:tag-placeholder="$t('invoice.supplier_details_article_new')"
												@input="onArticleChosen(line, $event)"
												@tag="onArticleChosen(line, $event)"
											>
												<template slot="option" slot-scope="{ option }">
													{{ option.isTag ? option.label : option }}
													<span v-if="article_hints[option]" class="invoice-supplier-details__option-hint">({{ article_hints[option] }})</span>
												</template>
												<template slot="noOptions">{{ $t('global.list_empty') }}</template>
											</e-select>
										</div>
									</div>
								</td>
							</tr>
							<tr>
								<td class="text-right">
									<span v-if="line.locked">{{ line.isdetails_qte }}</span>
									<b-input v-else v-select-on-focus :tabindex="index * 2 + 1" v-model="line.isdetails_qte" @blur.native="onQuantityOrPriceLeft(line)" />
								</td>
								<td>
									<span v-if="line.locked">{{ unitLabel(line.isdetails_unit) }}</span>
									<b-form-select v-else v-model="line.isdetails_unit" :options="unit_options" @change="refreshControl" />
								</td>
								<td class="text-right">
									<span v-if="line.locked">{{ line.isdetails_htunit }}</span>
									<PriceInput v-else :tabindex="index * 2 + 2" v-model="line.isdetails_htunit" @blur.native="onQuantityOrPriceLeft(line)" />
								</td>
								<td class="text-right" :title="hasAmountMismatch(line) ? $t('invoice.supplier_details_mismatch') : ''">
									<span v-if="line.locked">{{ line.isdetails_ht }}</span>
									<PriceInput v-else v-model="line.isdetails_ht" @change="refreshControl" />
								</td>
								<td class="text-right">
									<span v-if="line.locked">{{ line.isdetails_vat_percent }}</span>
									<b-input v-else v-select-on-focus v-model="line.isdetails_vat_percent" @change="refreshControl" />
								</td>
								<td class="text-center">
									<a v-if="!line.locked" href="#" @click.prevent="removeLine(line)">
										<font-awesome-icon :icon="['fal', 'trash-alt']" />
									</a>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<button v-if="!read_only" class="btn btn-secondary mt-3" @click="addLine">
					<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_details_add') }}
				</button>

				<div v-if="control" class="invoice-supplier-details__control mt-4">
					<h4>{{ $t('invoice.supplier_details_control') }}</h4>

					<table class="table table-sm mb-0 invoice-supplier-details__totals">
						<thead>
							<tr>
								<th></th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_invoice') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr :class="{ 'text-danger': !control.ht }">
								<th>{{ $t('invoice.suppliersocr_ht') }}</th>
								<td class="text-right">{{ amounts.invoice_ht }}</td>
								<td class="text-right">{{ amounts.lines_ht }}</td>
							</tr>
							<tr :class="{ 'text-danger': !control.vat }">
								<th>{{ $t('invoice.suppliersocr_ttc') }}</th>
								<td class="text-right">{{ amounts.invoice_ttc }}</td>
								<td class="text-right">{{ amounts.lines_ttc }}</td>
							</tr>
						</tbody>
					</table>

					<div class="mt-3">
						<ErrorAlert v-if="missing_label" messageI18n="invoice.supplier_details_missing_label" />
						<ErrorAlert v-else-if="!balanced" messageI18n="invoice.supplier_details_unbalanced" />

						<button class="btn btn-primary" :disabled="!balanced || missing_label || saving" @click="saveLines">
							{{ $t('invoice.supplier_details_save') }}
							<font-awesome-icon v-if="saving" :icon="['fal', 'spinner']" spin />
						</button>
					</div>

					<template v-if="rates.length">
						<!-- La référence par taux ne vient que de la lecture optique. Une facture reçue
						     par voie électronique n'en a aucune — sa prédiction se réduit à l'identité du
						     vendeur — et une facture lue peut n'avoir livré aucun taux. Le tableau reste
						     alors utile comme ventilation des lignes, mais il n'y a rien à confronter, et
						     rien à mettre en garde. -->
						<p v-if="has_reference" class="invoice-supplier-details__hint mt-3 mb-1">
							{{ $t('invoice.supplier_details_control_reference_origin') }}
						</p>

						<table class="table table-sm mb-0 mt-3">
							<thead>
								<tr>
									<th>{{ $t('invoice.supplier_details_vat') }}</th>
									<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
									<th v-if="has_reference" class="text-right">{{ $t('invoice.supplier_details_control_reference') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="rate in rates" :key="rate.key" :class="{ 'text-danger': rate.match === false }">
									<td>{{ rate.label }}</td>
									<td class="text-right">{{ rate.lines }}</td>
									<td v-if="has_reference" class="text-right">{{ rate.reference }}</td>
								</tr>
							</tbody>
						</table>
					</template>
				</div>
			</template>
		</div>
		</div>

	</div>
</template>


<script type="text/javascript">
	/* global BigInt */
	// Déclaré ici parce que la configuration ESLint du dépôt ne connaît que l'environnement node, sans
	// les globales d'ES2020. Le navigateur, lui, l'a : la cible de compilation ne descend pas plus bas.

	import Invoice from "@/mixins/Invoice.js"

	export default {
		name: "InvoiceSupplierFicheDetails",

		components: {
			ErrorAlert: () => import('GroomyRoot/components/Alert/ErrorAlert'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			PriceInput: () => import('GroomyRoot/components/Inputs/PriceInput')
		},

		mixins: [Invoice],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				loaded: false,
				saving: false,
				lines: [],
				// Articles déjà facturés par ce fournisseur, proposés sur le libellé des lignes.
				articles: [],
				control: null,
				// Montants du contrôle, formatés dans une méthode : priceFormat attend la devise, et un
				// computed rendrait des promesses.
				amounts: {
					lines_ht: '',
					invoice_ht: '',
					lines_ttc: '',
					invoice_ttc: ''
				},
				rates: []
			}
		},

		computed: {
			/**
			 * Dit si la ventilation par taux a quelque chose à quoi se confronter.
			 *
			 * La référence ne vient que de la lecture optique : une facture reçue par voie électronique
			 * n'en a pas, et une facture lue n'en a pas toujours. Sans elle, le tableau des taux n'est
			 * qu'une ventilation des lignes — l'annoncer comme une comparaison serait mentir.
			 */
			has_reference() {
				return this.rates.some(rate => rate.reference !== '')
			},

			/**
			 * Dit si une ligne attend encore son libellé.
			 *
			 * Le serveur l'exige, et il refuse l'envoi entier : sans ce contrôle, ajouter une ligne puis
			 * oublier de la nommer ferait échouer le dépôt de toutes les autres, avec un message qui ne
			 * dirait pas laquelle est en cause.
			 */
			missing_label() {
				return this.lines.some(line => !line.isdetails_label)
			},

			/**
			 * Dit si le détail peut être déposé.
			 *
			 * Les deux totaux doivent retomber sur ceux de la facture : le hors taxes, et la taxe — donc
			 * le toutes taxes, qui en est la somme. Tant que l'un des deux diverge, les lignes racontent
			 * autre chose que la facture, et les enregistrer ferait porter cette contradiction à la
			 * ventilation comptable puis à l'export.
			 */
			balanced() {
				// Sur une facture dont le vendeur a déclaré toutes les lignes, l'enregistrement ne touche
				// ni aux montants ni aux quantités : il ne porte que le nom de l'article, qui est à nous.
				// L'équilibre des totaux n'a alors rien à autoriser.
				if(this.read_only) return true

				return !!this.control && this.control.ht && this.control.vat
			},

			/**
			 * Nombre de colonnes du tableau — quantité, unité, prix unitaire, hors taxes, taux et la
			 * colonne de suppression — dont dépendent la rangée du libellé et le message d'absence de
			 * ligne.
			 */
			columns_count() {
				return 6
			},

			/**
			 * Le document reçu est tantôt un PDF, tantôt une image selon la source : le premier
			 * s'affiche dans un cadre, la seconde directement.
			 */
			is_pdf() {
				return !!this.invoice.suppliersocr_pdf_url && this.invoice.suppliersocr_pdf_url.includes('.pdf')
			},

			/**
			 * Le détail ne se corrige pas quand le vendeur l'a déclaré lui-même.
			 *
			 * C'est l'origine des lignes qui le dit, et non la provenance de la facture : un dépôt
			 * manuel rattaché après coup à son document électronique en porte les lignes, et elles font
			 * foi tout autant.
			 */
			read_only() {
				return this.lines.some(line => line.locked)
			},

			/**
			 * Articles déjà facturés par ce fournisseur, tels que la liste les propose.
			 *
			 * Ce sont leurs libellés seuls, et non les articles eux-mêmes : c'est le libellé que la ligne
			 * porte et sur lequel le serveur retrouve l'article, et le champ reste ouvert à un nom
			 * qu'aucun d'eux ne porte encore. Le serveur les rend déjà rangés.
			 */
			article_labels() {
				return this.articles.map(article => article.isarticles_label)
			},

			/**
			 * Précision affichée à côté de chaque suggestion : le code de l'article chez le vendeur, son
			 * unité, ou rien quand il n'en porte pas.
			 *
			 * Rangée par libellé une fois pour toutes plutôt que retrouvée à chaque suggestion affichée :
			 * la liste se rouvre à chaque ligne du tableau, et elle ne change qu'avec les articles.
			 */
			article_hints() {
				return Object.fromEntries(this.articles.map(article => [
					article.isarticles_label,
					[article.isarticles_code, this.unitLabel(article.isarticles_unit)].filter(part => part).join(' — ')
				]))
			},

			/**
			 * Unités proposées à la saisie, triées sur leur libellé.
			 *
			 * Les codes sont ceux d'UN/ECE Rec 20, le vocabulaire qu'une facture électronique déclare et
			 * que le serveur attend. La liste reprend celle de sa table de correspondance : un code
			 * qu'elle ne connaîtrait pas serait écarté à l'enregistrement, et l'unité disparaîtrait sans
			 * rien dire. Elle est énumérée ici plutôt que lue dans les traductions, dont le $t ne rend
			 * que des chaînes.
			 *
			 * Aucune entrée vide : une ligne modifiable porte toujours une unité, l'unité neutre à
			 * défaut d'une autre. Se remettre sur du vide ne mènerait qu'à la retrouver au rechargement.
			 */
			unit_options() {
				const codes = ['C62', 'KGM', 'GRM', 'TNE', 'LTR', 'MLT', 'MTK', 'MTQ', 'MTR', 'DAY', 'MON', 'HUR', 'KWH', 'SET', 'PR', 'BX', 'BG', 'CT', 'PF']
				const options = codes.map(code => ({ value: code, text: this.unitLabel(code) }))

				options.sort((a, b) => a.text.localeCompare(b.text))

				return options
			}
		},

		methods: {
			/**
			 * Charge les articles du fournisseur, les lignes de la facture, puis son contrôle.
			 *
			 * Les articles viennent en premier : ce sont eux que la saisie des libellés propose.
			 */
			async init_component() {
				this.loaded = false

				this.articles = await this.getInvoiceSupplierArticles(this.invoicesupplier_id) || []

				const lines = await this.getInvoiceSupplierDetails(this.invoicesupplier_id) || []
				this.lines = lines.map(line => this.prepareLine(line))

				await this.refreshControl()

				this.loaded = true
			},

			/**
			 * Met une ligne en état d'être éditée, et retient si elle est verrouillée.
			 */
			prepareLine(line) {
				const locked = line.isdetails_origin === 'xml'

				const prepared = {
					...line,
					isdetails_rebillable: !!line.isdetails_rebillable,
					// Une ligne qui n'annonce aucune unité part sur l'unité neutre. Le serveur écarte ce
					// que la lecture optique lui donne et qu'il ne reconnaît pas, si bien qu'une unité
					// absente ne se distingue pas d'une unité mal lue : une case vide n'apprend rien à qui
					// recontrôle la facture, et c'est le moment où il la corrige. Celle du vendeur est
					// laissée telle quelle — son document la déclare, elle ne se déduit pas ici.
					isdetails_unit: locked ? line.isdetails_unit : line.isdetails_unit || 'C62',
					// La base rend un décimal à cinq chiffres : sans cela le champ afficherait 2.00000.
					isdetails_qte: this.trimDecimals(line.isdetails_qte),
					isdetails_htunit: this.trimDecimals(line.isdetails_htunit),
					article_label: line.isdetails_article ? line.isdetails_article.isarticles_label : line.isdetails_label,
					article_renamed: !!line.isdetails_article && line.isdetails_article.isarticles_label !== line.isdetails_label,
					locked
				}

				return prepared
			},

			/**
			 * Ajoute une ligne vide au tableau. Elle n'existe qu'à l'écran jusqu'au dépôt de l'ensemble.
			 */
			addLine() {
				this.lines.push(this.prepareLine({
					isdetails_id: null,
					isdetails_label: '',
					isdetails_qte: null,
					isdetails_unit: null,
					isdetails_htunit: null,
					isdetails_ht: 0,
					isdetails_vat_percent: null,
					isdetails_start_date: null,
					isdetails_end_date: null,
					isdetails_horse: null,
					isdetails_rebillable: false,
					isdetails_origin: 'manual'
				}))
			},

			/**
			 * Ce que la ligne enverra au serveur, dans un ordre stable.
			 *
			 * Sert aussi de repère pour savoir si elle a bougé : c'est sa forme sérialisée qu'on compare
			 * à celle du dernier enregistrement.
			 */
			linePayload(line) {
				return {
					isdetails_id: line.isdetails_id,
					isdetails_label: line.isdetails_label,
					isdetails_article_label: line.article_label,
					isdetails_qte: this.toNumberOrNull(line.isdetails_qte),
					isdetails_unit: line.isdetails_unit,
					isdetails_htunit: this.toNumberOrNull(line.isdetails_htunit),
					isdetails_ht: this.toNumberOrNull(line.isdetails_ht) || 0,
					isdetails_vat_percent: this.toNumberOrNull(line.isdetails_vat_percent),
					// La période, le cheval et la refacturation ne se corrigent pas ici — ils tiendront
					// dans leur propre onglet, une fois la facture recontrôlée. Ils sont malgré tout
					// renvoyés tels qu'ils sont arrivés : le serveur réécrit ces colonnes à chaque dépôt,
					// et les taire reviendrait à les vider.
					isdetails_start_date: line.isdetails_start_date,
					isdetails_end_date: line.isdetails_end_date,
					isdetails_horse: line.isdetails_horse ? line.isdetails_horse.horse_id : null,
					isdetails_rebillable: line.isdetails_rebillable
				}
			},

			/**
			 * Dépose l'ensemble des lignes.
			 *
			 * Le bouton ne s'ouvre que lorsque les totaux retombent sur ceux de la facture : déposer un
			 * détail qui ne se réconcilie pas reviendrait à installer en base une incohérence que la
			 * ventilation comptable et l'export auraient ensuite à porter. L'écran travaille donc en
			 * local jusque-là, et n'écrit qu'une fois.
			 */
			async saveLines() {
				this.saving = true

				const saved = await this.saveInvoiceSupplierDetails(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line))
				)

				this.saving = false

				if(!saved) {
					this.failureToast("toast.info_save_failed")
					return
				}

				this.lines = saved.map(line => this.prepareLine(line))

				await this.refreshControl()

				this.successToast("toast.info_modif_succes")
				this.$emit('invoice-updated')
			},

			/**
			 * Le nom d'article vient d'être choisi dans la liste ou créé : il cesse de suivre le libellé
			 * de la ligne.
			 *
			 * Les deux gestes passent par le même chemin parce qu'une création n'affecte pas la valeur
			 * d'elle-même : la liste se contente de la signaler, et c'est bien le nom saisi qui devient
			 * celui de l'article, comme s'il avait été pris parmi les autres.
			 *
			 * Repasser le nom d'article à celui de la ligne les réunit à nouveau, plutôt que de figer un
			 * lien rompu sur une égalité retrouvée par hasard.
			 */
			onArticleChosen(line, label) {
				line.article_label = label
				line.article_renamed = label !== line.isdetails_label
			},

			/**
			 * Le nom d'article suit le libellé de la ligne tant qu'il n'en a pas été distingué.
			 *
			 * C'est le comportement attendu par défaut : on corrige un libellé mal lu, et l'article se
			 * range sous ce nom. Dès que l'utilisateur a nommé l'article autrement — pour rapprocher
			 * deux factures mensuelles dont le vendeur date le libellé, par exemple — ce nom lui
			 * appartient, et corriger la ligne ne le défait plus.
			 */
			onLabelChanged(line) {
				if(!line.article_renamed) {
					line.article_label = line.isdetails_label
				}
			},

			/**
			 * Recalcule le montant hors taxes en quittant la quantité ou le prix unitaire.
			 *
			 * Le déclencheur est la sortie du champ, et non sa modification : la lecture optique se
			 * trompe sur une partie des lignes, et le geste de contrôle consiste justement à parcourir
			 * la quantité et le prix unitaire au clavier. Exiger une réécriture laisserait en place un
			 * montant faux dont les deux facteurs sont pourtant justes.
			 *
			 * Le produit est fait en entiers exacts : en virgule flottante, 11 × 30 tombe juste mais
			 * 3 × 0,1 vaut 0,30000000000000004, et un prix à quatre décimales posé sur un demi-centime
			 * — 1 × 1,0050 — s'arrondit à l'envers parce que la machine le représente en dessous de sa
			 * valeur. Les deux nombres sont donc lus tels qu'ils sont écrits, multipliés sans perte,
			 * puis arrondis une seule fois.
			 *
			 * L'enregistrement ne part que si la ligne a effectivement bougé : traverser le tableau sans
			 * rien corriger ne déclenche aucun appel.
			 */
			onQuantityOrPriceLeft(line) {
				const cents = this.multiplyToCents(line.isdetails_qte, line.isdetails_htunit)

				if(cents !== null) {
					line.isdetails_ht = (Number(cents) / 100).toFixed(2)
				}

				this.refreshControl()
			},

			/**
			 * Lit un nombre écrit en toutes lettres, virgule ou point, et le rend sous forme d'entier
			 * accompagné du nombre de décimales qu'il portait.
			 *
			 * @return {Object|null} {value: BigInt, scale: Number}, ou null si ce n'est pas un nombre.
			 */
			parseDecimal(value) {
				if(value === null || value === undefined || value === '') return null

				const text = String(value).trim().replace(',', '.')

				if(!/^-?\d*\.?\d*$/.test(text) || !/\d/.test(text)) return null

				const negative = text.charAt(0) === '-'
				const parts = text.replace('-', '').split('.')
				const whole = parts[0] || '0'
				const fraction = parts[1] || ''
				const digits = BigInt(whole + fraction)

				return { value: negative ? -digits : digits, scale: fraction.length }
			},

			/**
			 * Dit si le montant d'une ligne contredit ses propres facteurs.
			 *
			 * C'est le signe le plus sûr d'une lecture optique fautive : sur les factures déjà lues,
			 * quantité fois prix unitaire ne retombe pas sur le total relevé une fois sur deux. Le
			 * rapprochement se fait en centimes entiers, pour qu'un écart d'arrondi ne passe pas pour
			 * une erreur.
			 *
			 * Rien n'est signalé quand l'un des trois manque : une ligne dont le prix unitaire n'a pas
			 * été lu n'est pas incohérente, elle est incomplète.
			 */
			hasAmountMismatch(line) {
				// Le prix unitaire s'entend pour isdetails_base_qty unités : un vendeur qui tarife aux
				// cent pièces déclare un prix que la quantité seule ne retrouve pas. Plutôt que de
				// diviser — ce qui rouvrirait un arrondi — les deux membres sont multipliés en croix,
				// et la comparaison reste exacte.
				const product = this.multiplyToCents(line.isdetails_qte, line.isdetails_htunit)
				const amount = this.multiplyToCents(line.isdetails_ht, line.isdetails_base_qty || '1')

				if(product === null || amount === null) return false

				return product !== amount
			},

			/**
			 * Puissance de dix, en entier exact.
			 *
			 * Écrite plutôt que calculée : l'opérateur d'exponentiation est réécrit en Math.pow à la
			 * compilation, et Math.pow refuse les entiers exacts.
			 *
			 * @param {Number} exponent
			 * @return {BigInt}
			 */
			powerOfTen(exponent) {
				return BigInt('1' + '0'.repeat(exponent))
			},

			/**
			 * Produit de deux nombres décimaux, rendu en centimes entiers.
			 *
			 * @return {BigInt|null} Null dès que l'un des deux n'est pas un nombre.
			 */
			multiplyToCents(quantity, unit_price) {
				const a = this.parseDecimal(quantity)
				const b = this.parseDecimal(unit_price)

				if(a === null || b === null) return null

				const product = a.value * b.value
				const scale = a.scale + b.scale

				// Moins de deux décimales : le produit est déjà exact, il ne reste qu'à le porter en
				// centimes.
				if(scale <= 2) {
					return product * this.powerOfTen(2 - scale)
				}

				// Au-delà, l'arrondi se fait sur le reste de la division entière, au demi supérieur en
				// valeur absolue — le signe est mis de côté pour qu'un avoir s'arrondisse comme une
				// facture.
				const divisor = this.powerOfTen(scale - 2)
				const negative = product < BigInt(0)
				const absolute = negative ? -product : product
				const quotient = absolute / divisor
				const remainder = absolute % divisor
				const rounded = remainder * BigInt(2) >= divisor ? quotient + BigInt(1) : quotient

				return negative ? -rounded : rounded
			},

			/**
			 * Retire les zéros décimaux que la base ajoute à un décimal.
			 *
			 * @param {String|Number|null} value
			 * @return {String|null}
			 */
			trimDecimals(value) {
				if(value === null || value === undefined || value === '') return value

				const parsed = parseFloat(value)

				return isNaN(parsed) ? value : String(parsed)
			},

			/**
			 * Nombre attendu par le serveur, à partir d'une saisie qui peut porter une virgule décimale
			 * ou être vide.
			 */
			toNumberOrNull(value) {
				if(value === null || value === undefined || value === '') return null

				const parsed = parseFloat(String(value).replace(',', '.'))

				return isNaN(parsed) ? null : parsed
			},

			/**
			 * Retire une ligne du tableau. Rien n'est perdu tant que l'ensemble n'a pas été déposé :
			 * quitter l'onglet sans enregistrer la ramène.
			 */
			removeLine(line) {
				this.lines = this.lines.filter(other => other !== line)

				this.refreshControl()
			},

			/**
			 * Reprend le contrôle des sommes sur l'état de l'écran, et prépare son affichage.
			 *
			 * Il est rejoué à chaque geste : c'est lui qui ouvre ou ferme l'enregistrement, il ne peut
			 * donc pas porter sur ce qui est en base pendant que l'utilisateur corrige.
			 */
			async refreshControl() {
				this.control = await this.checkInvoiceSupplierDetailsSum(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line))
				)

				if(!this.control) return

				const currency = this.invoice.suppliersocr_currency || 'EUR'

				this.amounts = {
					lines_ht: await this.priceFormat(this.control.lines_ht, currency, true),
					invoice_ht: await this.priceFormat(this.control.invoice_ht, currency, true),
					lines_ttc: await this.priceFormat(this.control.lines_ttc, currency, true),
					invoice_ttc: await this.priceFormat(this.control.invoice_ttc, currency, true)
				}

				const rates = []

				for(const rate of this.control.rates) {
					// La référence se lit sur la base hors taxes quand le document l'a donnée, et sur le
					// montant de taxe sinon : c'est ce que le serveur a comparé.
					const on_ht = rate.reference_ht !== null
					const reference = on_ht ? rate.reference_ht : rate.reference_vat

					rates.push({
						key: rate.rate === null ? 'null' : rate.rate,
						label: rate.rate === null ? this.$t('invoice.supplier_details_control_no_rate') : rate.rate + ' %',
						lines: await this.priceFormat(on_ht ? rate.lines_ht : rate.lines_vat, currency, true),
						reference: reference === null ? '' : await this.priceFormat(reference, currency, true),
						match: rate.match
					})
				}

				this.rates = rates
			},

			/**
			 * Libellé d'un code d'unité.
			 *
			 * Le document d'un vendeur déclare son unité dans toute l'étendue de la norme Rec 20, dont
			 * nos libellés ne couvrent que les codes rencontrés jusqu'ici. Un code inconnu est rendu tel
			 * quel : il reste lisible, là où la clé de traduction non résolue s'afficherait brute.
			 */
			unitLabel(code) {
				if(!code) return ''

				const key = 'invoice.supplier_details_units.' + code
				const label = this.$t(key)

				return label === key ? code : label
			}
		},

		mounted() {
			this.init_component()
		}
	}
</script>
